import { UseMutateAsyncFunction } from '@tanstack/react-query';
import {
  ApprovalItemModel,
  ApprovalItemMutationInput,
  useCreateApprovalItem,
} from 'api-hooks/approval-item';
import { useGetMe } from 'api-hooks/auth/query';
import { ApprovalableTypeEnum, FileInputType } from 'api-hooks/common';
import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ROLE,
  SIZE,
} from 'baseui/modal';
import formSetErrors from 'common/helpers/form-setError';
import { FilesYupSchema } from 'common/helpers/validation';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { MasterInput } from 'components/elements/master';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import { DialogCommonStyles } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

interface Props {
  onClose: () => void;
  onSubmit?: (props: {
    input: ApprovalItemMutationInput;
    form: ReturnType<typeof useForm>;
    createApproval: UseMutateAsyncFunction<
      ApiResult<ApprovalItemModel>,
      ApiError,
      ApprovalItemMutationInput,
      unknown
    >;
  }) => Promise<any>;
  data: {
    approvalableId: string;
    approvalableType: ApprovalableTypeEnum;
    workflow?: string | null;
  };
  onDismissSuccess?: () => void;
  onDismissCancel?: () => void;
}

type FormType = {
  approvalableId: string;
  approvalableType: ApprovalableTypeEnum;
  workflow?: string | null;
  reason: string;
  files: FileInputType[];
};

export default function RequestApprovalModal(props: Props) {
  const { t } = useTranslation();
  const { data, onClose, onDismissSuccess, onDismissCancel } = props;
  const { mutateAsync, isLoading } = useCreateApprovalItem();
  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;

  const defaultValues = React.useMemo<FormType>(
    () => ({
      ...data,
      reason: '',
      files: [],
    }),
    [data],
  );

  const resolver = useYupValidationResolver(
    Yup.object()
      .shape({
        reason: Yup.string().required(),
        files: FilesYupSchema.required(),
      })
      .required(),
  );

  const methods = useForm<FormType>({
    defaultValues,
    resolver,
    mode: 'onChange',
  });

  const onSubmit = async (values: FormType) => {
    try {
      if (props.onSubmit) {
        props.onSubmit({
          input: values,
          form: methods as any,
          createApproval: mutateAsync,
        });
      } else {
        const result = await mutateAsync(values);
        notification.success({ message: result.message });
        onDismissSuccess?.();
        onClose();
      }
    } catch (e) {
      if (e.errors) {
        formSetErrors(e.errors, methods.setError);
      }
      notification.error({ message: e.message });
    }
  };

  const onCancel = () => {
    onClose();
    onDismissCancel?.();
  };

  return (
    <Modal
      closeable={false}
      isOpen
      animate
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      size={SIZE.default}
      role={ROLE.dialog}
      overrides={DialogCommonStyles}
    >
      <ModalHeader>{t('common:request_approval')}</ModalHeader>
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalBody>
          <MasterInput
            disabled
            type="text"
            label={t('common:request_from')}
            value={employeeHook?.name || ''}
          />
          <Input
            type="text-area"
            name="reason"
            label={t('common:reason')}
            required
          />
          <Input
            type="files"
            name="files"
            label={t('common:upload_document')}
            valueKey="file"
            previewKey="url"
            required
            fileType={UploadFileTypeEnum.ApprovalItem}
          />
        </ModalBody>
        <ModalFooter $style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            buttonType="tertiary"
            onClick={onCancel}
            disabled={methods.formState.isSubmitting}
          >
            {t('common:cancel')}
          </Button>
          <Separator gap={8} />
          <Input
            type="submit"
            isLoading={methods.formState.isSubmitting || isLoading}
            disabled={methods.formState.isSubmitting || isLoading}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
}
